<template>
  <SocialMentionCard
    v-bind="{ ...$props, ...$attrs }"
    class="pointer"
    :icon="socialIcon(mention.type)"
    :list-title="listTitle"
    :title="mention.title"
    @click="$emit('click', $event)"
    @mention-removed="removeMention"
    @mention-selected="$emit('mention-selected', $event)"
  >
    <template #header>
      <span class="title">
        <strong>
          <AuthorWidget
            v-if="mention.author"
            :disable-click="options.disableAuthorClick"
            :mention="mention"
          />
        </strong>

        <QIcon
          v-if="mention.author_verified"
          class="text-social-verified social-verified"
          name="ion-checkmark-circle"
          size="14px"
        />
        <strong v-else> · </strong>

        <SocialMentionCardFollowerCount
          v-if="mention.author_follower_count"
          display-separator
          :follower-count="mention.author_follower_count"
        />

        <span
          v-if="isReply"
          class="text-blue-grey bold"
        >
          <QIcon
            name="img:https://assets.streem.com.au/icons/comments.svg"
            size="10px"
          />
          {{ $t("social_mention_card_tweet.replied") }}
        </span>

        <span
          v-if="isRetweet"
          class="text-blue-grey bold"
        >
          <QIcon
            name="img:https://assets.streem.com.au/icons/reshare.svg"
            size="10px"
          />
          {{ $t("social_mention_card_tweet.retweeted") }}
        </span>

        <TimeAgo
          class="text-blue-grey subtitle self-start"
          :date="mention.timestamp"
        />
      </span>
    </template>

    <template #body>
      <MentionExcerpts
        v-if="!hasOriginalTweet || isQuoteOrReply"
        expandable
        :mention="mention"
        :no-highlighting="noHighlighting"
        social-stream
      />

      <div
        v-if="hasOriginalTweet"
        class="original-tweet-card"
        :class="isQuoteOrReply && 'q-my-md'"
      >
        <SocialMentionCardTweet
          hide-border-color
          :hide-engagement="options.engagementDisabled"
          :mention="mention.original_tweet"
          :options="options"
          :stream="stream"
        />
      </div>
    </template>

    <slot />

    <template
      v-if="attachments.length"
      #attachments
    >
      <SocialMentionCardAttachedMedia
        :assets="videos"
        :mention="mention"
      />

      <SocialMentionCardAttachedMedia
        :assets="photos"
        :mention="mention"
      />
    </template>

    <template #engagement-items>
      <SocialMentionEngagementItem
        icon="likes"
        :model-value="mention.favorite_count"
        @click="openLink(mention.liked_by_url)"
      />

      <SocialMentionEngagementItem
        icon="comments"
        :model-value="mention.reply_count"
        @click="openLink(mention.status_url)"
      />

      <SocialMentionEngagementItem
        icon="reshare"
        :model-value="mention.retweet_count"
        @click="openLink(mention.retweeted_by_url)"
      />

      <SocialMentionEngagementItem
        icon="quoted"
        :model-value="mention.quote_count"
        @click="openLink(mention.quoted_by_url)"
      />
    </template>
  </SocialMentionCard>
</template>

<script>
import AuthorWidget from "shared/components/AuthorWidget.vue";
import { propsExcept, SocialMentionCard } from "shared/components/MentionCard";
import MentionExcerpts from "shared/components/MentionExcerpts.vue";
import SocialMentionCardAttachedMedia from "shared/components/SocialMentionCardAttachedMedia.vue";
import SocialMentionCardFollowerCount from "shared/components/SocialMentionCardFollowerCount.vue";
import SocialMentionEngagementItem from "shared/components/SocialMentionEngagementItem.vue";
import { openLink } from "shared/helpers/external";
import { socialIcon } from "shared/helpers/media";

export default {
  name: "SocialMentionCardTweet",
  components: {
    AuthorWidget,
    SocialMentionCard,
    MentionExcerpts,
    SocialMentionEngagementItem,
    SocialMentionCardAttachedMedia,
    SocialMentionCardFollowerCount,
  },
  props: {
    ...propsExcept(["listTitle"]),
  },
  emits: ["click", "mention-selected", "mention-removed"],
  computed: {
    isRetweet() {
      return this.mention.tweet_type === "retweet";
    },
    isReply() {
      return this.mention.tweet_type === "reply";
    },
    isQuoteOrReply() {
      return ["quote", "reply"].includes(this.mention.tweet_type);
    },
    hasOriginalTweet() {
      return Boolean(this.mention.original_tweet);
    },
    listTitle() {
      return this.mention.author;
    },
    attachments() {
      return this.mention.tweet_assets;
    },
    photos() {
      const photos = [];

      this.attachments.forEach((attachment) => {
        if (attachment.media_type === "photo") {
          photos.push({
            type: "photo",
            thumbnail: attachment.media_url,
          });
        }
      });

      return photos;
    },
    videos() {
      const videos = [];

      this.attachments.forEach((attachment) => {
        if (["video", "animated_gif"].includes(attachment.media_type)) {
          videos.push({
            type: attachment.media_type,
            thumbnail: attachment.media_url,
            url: attachment.url,
            duration: attachment.duration_in_milliseconds / 1000,
          });
        }
      });

      return videos;
    },
  },
  methods: {
    socialIcon,
    openLink,
    removeMention(mention) {
      this.$emit("mention-removed", mention);
    },
  },
};
</script>

<style lang="scss" scoped>
.social-icon {
  &.ion-checkmark-circle {
    color: rgb(73 208 103 / 50%);
  }

  &.ion-close-circle {
    color: rgb(249 88 88 / 50%);
  }

  &.ion-remove-circle {
    color: rgb(50 159 236 / 50%);
  }
}

.q-icon {
  vertical-align: 0;
}

.social-verified {
  margin: 0 5px 0 2px;
}

.original-tweet-card {
  border-radius: 4px;
}
</style>
